import { Component, OnInit } from '@angular/core';
import { Room } from 'src/app/classes/Room';
import { RoomService } from '../../../services/room.service';
import { FormGroup } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ConfirmDialogComponent } from '../../dialog/confirm-dialog/confirm-dialog.component';
import { RoomEditComponent } from '../room-edit/room-edit.component';
import { Office } from '../../../classes/Office';
import { OfficeService } from '../../../services/office.service';
import { ZoomOAuthService } from 'src/app/services/zoom.service';
import { BehaviorSubject } from 'rxjs';
import { ZoomPermissions } from 'src/app/classes/ZoomPermissions';
import { ZoomOAuth } from 'src/app/classes/ZoomOAuth';
import { RoomColor } from '../../../enums/RoomColor';
import {
  getRoomColorHex as getRoomColorHexValue,
  getRoomColorTextHex as getRoomColorTextHexValue
} from '../../../util/room-color.util';

@Component({
  selector: 'app-room-list',
  styleUrls: ['./room-list.component.scss'],
  templateUrl: './room-list.component.html',
})
export class RoomListComponent implements OnInit {

  public rooms: Room[];
  public errorMessage: string;
  private currentDialog = null;
  private offices: Map<number, Office>;
  private permissionsSubject: Map<number, BehaviorSubject<ZoomPermissions>> = new Map();
  private zoomLinksSubject: Map<number, BehaviorSubject<ZoomOAuth>> = new Map();

  constructor(private roomService: RoomService,
              private modalService: NgbModal,
              private officeService: OfficeService,
              private zoomOAuthService: ZoomOAuthService) {
  }

  ngOnInit(): void {
    this.updateList();

    this.officeService.getAll()
      .subscribe((response: Office[]) => this.offices = new Map(response.map(office => [office.id, office])));
  }

  openEditModal(room: Room): void {
    this.currentDialog = this.modalService.open(RoomEditComponent,
      {
        scrollable: true,
        windowClass: 'modal-room-edit',
        size: 'xl',
        centered: true,
      });

    this.currentDialog.componentInstance.room = room;

    this.currentDialog.result.then(
      (result) => {
        if (result instanceof FormGroup) {
          const editedRoom = new Room(result.value);

          const formData = new FormData();
          if (result.get('imageFile').value) {
            formData.append('image', result.get('imageFile').value, result.get('imageFile').value.name);
          }
          formData.append('room', new Blob([JSON.stringify(editedRoom)], { type: 'application/json' }));

          this.roomService.addOrEdit(formData).subscribe({
            next: () => this.updateList(),
            error: (error) => this.errorMessage = error.error.error
          });
        }
      },
      (error) => {
        console.log('Dialog close error: ' + error);
      }
    );
  }

  openDeleteConfirmModal(room: Room): void {
    this.currentDialog = this.modalService.open(ConfirmDialogComponent,
      {
        scrollable: true,
        windowClass: 'modal-confirm',
        size: 'md',
        centered: true,
        // keyboard: false,
        // backdrop: 'static'
      });

    this.currentDialog.componentInstance.message =
      'Are you sure that you want to delete room ' + room.name + '?';

    this.currentDialog.result.then((result) => {
      if (result.status === 'confirmed') {
        this.roomService.delete(room.id).subscribe({
          next: () => this.updateList(),
          error: (error) => this.errorMessage = error.error.error
        });
      }
    }, (error) => {
      console.log('Dialog close error: ' + error);
    });
  }

  getOffice(officeId: number): string {
    const office = this.offices.get(officeId);
    return office.city + ', ' + office.address;
  }

  getRoomColorHex(color: RoomColor | string): string {
    return getRoomColorHexValue(color);
  }

  getRoomColorTextHex(color: RoomColor | string): string {
    return getRoomColorTextHexValue(color);
  }

  isZoomPermissionGranted(roomId): BehaviorSubject<ZoomPermissions> {
    if (!this.permissionsSubject.has(roomId)) {
      const permission$ = new BehaviorSubject<ZoomPermissions>({ result: false }); // initial value
      this.permissionsSubject.set(roomId, permission$);
      this.zoomOAuthService.isPermissionGranted(roomId).subscribe(permission => permission$.next(permission));
    }
    return this.permissionsSubject.get(roomId);
  }

  getLink(roomId): BehaviorSubject<ZoomOAuth> {
    if (!this.zoomLinksSubject.has(roomId)) {
      const link$ = new BehaviorSubject<ZoomOAuth>({ url: '' });
      this.zoomLinksSubject.set(roomId, link$);
      this.zoomOAuthService.getOAuthLink(roomId).subscribe(link => link$.next(link));
    }
    return this.zoomLinksSubject.get(roomId);
  }

  private updateList(): void {
    this.roomService.getAll()
      .subscribe({
        next: (response: Room[]) => this.rooms = response,
        error: (error) => this.errorMessage = error.error.error
      });
  }
}
